import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(null);

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
            return;
        }
        
        // Verify token validity
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            // Simulate API call to verify token
            await new Promise(resolve => setTimeout(resolve, 1000));
            setTokenValid(true);
        } catch (error) {
            setTokenValid(false);
        }
    };

    const validatePassword = (password) => {
        const requirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        return requirements;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear errors on change
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = {};

        // Validate new password
        const passwordReqs = validatePassword(formData.newPassword);
        if (!Object.values(passwordReqs).every(v => v)) {
            newErrors.newPassword = 'Password does not meet all requirements';
        }

        // Validate confirm password
        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            setSuccess(true);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error) {
            setErrors({ submit: 'Failed to reset password. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const passwordRequirements = validatePassword(formData.newPassword);

    if (tokenValid === null) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Verifying reset link...</p>
                </div>
            </div>
        );
    }

    if (tokenValid === false || !token) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.errorIcon}>
                        <XCircle size={64} color="#E74C3C" />
                    </div>
                    <h1 style={styles.errorTitle}>Invalid Reset Link</h1>
                    <p style={styles.errorText}>
                        This password reset link is invalid or has expired. Please request a new one.
                    </p>
                    <button onClick={() => navigate('/login')} style={styles.backButton}>
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.successIcon}>
                        <CheckCircle size={64} color="#27B05B" />
                    </div>
                    <h1 style={styles.successTitle}>Password Reset Successful!</h1>
                    <p style={styles.successText}>
                        Your password has been reset successfully. You can now log in with your new password.
                    </p>
                    <p style={styles.redirectText}>Redirecting to login page...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.iconContainer}>
                    <Lock size={48} color="#27B05B" />
                </div>
                
                <h1 style={styles.title}>Reset Password</h1>
                <p style={styles.subtitle}>Enter your new password below</p>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>New Password</label>
                        <div style={styles.inputWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                style={{
                                    ...styles.input,
                                    ...(errors.newPassword ? styles.inputError : {})
                                }}
                                placeholder="Enter new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={styles.eyeButton}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors.newPassword && (
                            <span style={styles.errorMessage}>{errors.newPassword}</span>
                        )}

                        <div style={styles.requirementsBox}>
                            <div style={styles.requirementItem}>
                                {passwordRequirements.length ? 
                                    <CheckCircle size={16} color="#27B05B" /> : 
                                    <XCircle size={16} color="#E0E0E0" />
                                }
                                <span style={{
                                    color: passwordRequirements.length ? '#27B05B' : '#999'
                                }}>
                                    At least 8 characters
                                </span>
                            </div>
                            <div style={styles.requirementItem}>
                                {passwordRequirements.uppercase ? 
                                    <CheckCircle size={16} color="#27B05B" /> : 
                                    <XCircle size={16} color="#E0E0E0" />
                                }
                                <span style={{
                                    color: passwordRequirements.uppercase ? '#27B05B' : '#999'
                                }}>
                                    One uppercase letter
                                </span>
                            </div>
                            <div style={styles.requirementItem}>
                                {passwordRequirements.lowercase ? 
                                    <CheckCircle size={16} color="#27B05B" /> : 
                                    <XCircle size={16} color="#E0E0E0" />
                                }
                                <span style={{
                                    color: passwordRequirements.lowercase ? '#27B05B' : '#999'
                                }}>
                                    One lowercase letter
                                </span>
                            </div>
                            <div style={styles.requirementItem}>
                                {passwordRequirements.number ? 
                                    <CheckCircle size={16} color="#27B05B" /> : 
                                    <XCircle size={16} color="#E0E0E0" />
                                }
                                <span style={{
                                    color: passwordRequirements.number ? '#27B05B' : '#999'
                                }}>
                                    One number
                                </span>
                            </div>
                            <div style={styles.requirementItem}>
                                {passwordRequirements.special ? 
                                    <CheckCircle size={16} color="#27B05B" /> : 
                                    <XCircle size={16} color="#E0E0E0" />
                                }
                                <span style={{
                                    color: passwordRequirements.special ? '#27B05B' : '#999'
                                }}>
                                    One special character
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Confirm New Password</label>
                        <div style={styles.inputWrapper}>
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                style={{
                                    ...styles.input,
                                    ...(errors.confirmPassword ? styles.inputError : {})
                                }}
                                placeholder="Confirm new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                style={styles.eyeButton}
                            >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <span style={styles.errorMessage}>{errors.confirmPassword}</span>
                        )}
                    </div>

                    {errors.submit && (
                        <div style={styles.submitError}>
                            <XCircle size={18} />
                            {errors.submit}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...styles.submitButton,
                            ...(loading ? styles.submitButtonDisabled : {})
                        }}
                    >
                        {loading ? 'Resetting Password...' : 'Reset Password'}
                    </button>
                </form>

                <div style={styles.footer}>
                    <button onClick={() => navigate('/login')} style={styles.linkButton}>
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#F5F5F5'
    },
    card: {
        width: '100%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        textAlign: 'center'
    },
    iconContainer: {
        width: '80px',
        height: '80px',
        margin: '0 auto 20px',
        borderRadius: '50%',
        backgroundColor: '#E8F5E9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '10px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#666',
        marginBottom: '30px'
    },
    form: {
        textAlign: 'left'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#2C3E50',
        marginBottom: '8px'
    },
    inputWrapper: {
        position: 'relative'
    },
    input: {
        width: '100%',
        padding: '12px 45px 12px 12px',
        fontSize: '15px',
        border: '2px solid #E0E0E0',
        borderRadius: '8px',
        transition: 'border-color 0.3s ease',
        boxSizing: 'border-box'
    },
    inputError: {
        borderColor: '#E74C3C'
    },
    eyeButton: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#999',
        padding: '5px'
    },
    errorMessage: {
        display: 'block',
        marginTop: '5px',
        fontSize: '13px',
        color: '#E74C3C'
    },
    requirementsBox: {
        marginTop: '12px',
        padding: '15px',
        backgroundColor: '#F9F9F9',
        borderRadius: '8px'
    },
    requirementItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '6px 0',
        fontSize: '14px'
    },
    submitError: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        backgroundColor: '#FFEBEE',
        color: '#E74C3C',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px'
    },
    submitButton: {
        width: '100%',
        padding: '15px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    submitButtonDisabled: {
        backgroundColor: '#95A5A6',
        cursor: 'not-allowed'
    },
    footer: {
        marginTop: '25px',
        paddingTop: '20px',
        borderTop: '1px solid #E0E0E0'
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#3498DB',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'underline'
    },
    successIcon: {
        width: '80px',
        height: '80px',
        margin: '0 auto 20px',
        borderRadius: '50%',
        backgroundColor: '#E8F5E9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    successTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#27B05B',
        marginBottom: '15px'
    },
    successText: {
        fontSize: '16px',
        color: '#2C3E50',
        marginBottom: '15px',
        lineHeight: '1.6'
    },
    redirectText: {
        fontSize: '14px',
        color: '#999',
        fontStyle: 'italic'
    },
    errorIcon: {
        width: '80px',
        height: '80px',
        margin: '0 auto 20px',
        borderRadius: '50%',
        backgroundColor: '#FFEBEE',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    errorTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#E74C3C',
        marginBottom: '15px'
    },
    errorText: {
        fontSize: '16px',
        color: '#2C3E50',
        marginBottom: '30px',
        lineHeight: '1.6'
    },
    backButton: {
        padding: '14px 40px',
        backgroundColor: '#3498DB',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer'
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    }
};

export default ResetPassword;
